import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import useMediaQuery from "@/hooks/useMediaQuery";
import { urlFor } from "@/sanity/lib/image";
import styles from "./HeroSlider.module.scss";
import Button from "@/components/Button";
import ArrowRight from "/packages/icons/arrow-right.svg";
import ArrowLeft from "/packages/icons/arrow-left.svg";

const Swiper = dynamic(() => import("swiper/react").then((mod) => mod.Swiper), {
  ssr: false,
});
import { SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export default function HomepageHero({ slides }) {
  const [isHydrated, setIsHydrated] = useState(false);
  const firstSlide = slides[0];
  const isMobile = useMediaQuery(768);
  const preloadImage = urlFor(firstSlide.mobileBackgroundImage)
    .width(768)
    .quality(75)
    .auto("format")
    .url();

  useEffect(() => {
    const vh = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty("--vh", `${vh * 0.01}px`);

    // After hydration, hide static slide
    setIsHydrated(true);
  }, []);

  const showSwiper = isHydrated && isMobile !== undefined;

  return (
    <>
      <Head>
        <link rel="preload" as="image" href={preloadImage} />
      </Head>

      <section className={styles.heroWrapper}>
        <div
          className={`${styles.sliderWrapper} ${isHydrated ? styles.swiperIsHydrated : ""
            }`}
        >
          {/* Static Slide for Initial Load */}
          <div className={styles.staticSlide}>
            <HeroSlide slide={firstSlide} index={0} preloadImage={preloadImage} isMobile={isMobile} />
          </div>

          {showSwiper && (
            <>
              {/* Navigation Buttons */}
              <button
                id="hero-prev"
                className={styles.prev}
                aria-label="Previous Slide"
              >
                <ArrowLeft />
              </button>
              <button
                id="hero-next"
                className={styles.next}
                aria-label="Next Slide"
              >
                <ArrowRight />
              </button>

              <Swiper
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                loop
                keyboard={{ enabled: true }}
                pagination={{ clickable: true, el: "#hero-pagination" }}
                navigation={{ prevEl: "#hero-prev", nextEl: "#hero-next" }}
                className={styles.swiper}
              >
                {slides.map((slide, index) => (
                  <SwiperSlide key={index} className={styles.swiperSlide}>
                    <HeroSlide slide={slide} index={index} isMobile={isMobile} />
                  </SwiperSlide>
                ))}
              </Swiper>
              <div id="hero-pagination" className={styles.bullets}></div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function HeroSlide({ slide, index, preloadImage, isMobile }) {
  const imageUrl = urlFor(
    !isMobile ? slide.backgroundImage : slide.mobileBackgroundImage
  )
    .width(!isMobile ? 1600 : 768)
    .quality(75)
    .auto("format")
    .url();


  return (
    <div className={styles.overlay}>
      <Image
        alt={slide.title || `Slide ${index + 1}`}
        src={preloadImage || imageUrl}
        fill
        priority={index === 0}
        fetchPriority={index === 0 ? "high" : undefined}
        sizes="(max-width: 768px) 100vw, 1600px"
        placeholder="empty"
        style={{ objectFit: "cover" }}
      />
      <div className="container">
        <div className={styles.hero__content__wrapper}>
          <div className={styles.hero__content}>
            {slide.title && (
              <h2
                className={`${styles.hero__title} ${slide.uppercaseTitle ? styles.uppercase : ""
                  }`}
              >
                {slide.title}
              </h2>
            )}
            {slide.body && (
              <p
                className={`${styles.hero__body} ${slide.uppercaseBody ? styles.uppercase : ""
                  }`}
              >
                {slide.body}
              </p>
            )}
            {slide.buttonUrl && (
              <Button
                type="link"
                href={slide.buttonUrl}
                className="button hero"
              >
                {slide.buttonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
